import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Card, Heading } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { mediaQueries } from '@eti/styles';
import Media from 'react-media';
import { PriceFormat } from '@eti/types';
import { formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import StickySidebar from '../../../../widgets/sidebar/components/StickySidebar';
import { usePortal } from '../../../../common/custom-hooks/portal';
import CartPriceSummary from '../../../../widgets/cart/components/CartPriceSummary';
import { PAYMENT } from '../../../../constants/formNamesConstants';
import { reduxFields } from '../../constants/formFields';
import PaymentLinkCartItems from './PaymentLinkCartItems';
import { propTypes } from './propTypes';

const { INSTALMENT_FEE, PRICE_IN_CHARGE_CURRENCY, SHOULD_ADD_INSTALMENT_FEE_IN_TOTAL_AMOUNT } =
  reduxFields;

const Cart = ({
  cartPrice,
  chargeCurrencyPriceFormat,
  discountAmount,
  instalmentFee,
  isOtherChargeCurrency,
  paymentMethodPrices,
  priceInChargeCurrency,
  products,
  selectedPaymentMethod,
  shouldAddInstallmentFeeInTotalAmount,
  shouldShowAllProducts,
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <Card.Section>
        <Heading data-testid="paymentLinkPage-cart-title" level={2}>
          {t('PaymentLink.Cart.Header')}
        </Heading>
      </Card.Section>
      <Card.Section>
        <PaymentLinkCartItems products={products} shouldShowAllProducts={shouldShowAllProducts} />
      </Card.Section>
      <Card.Section>
        <CartPriceSummary
          chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
          dataTestId="paymentLinkPage-cart-priceSummary"
          discountAmount={discountAmount}
          instalmentFee={instalmentFee}
          isOtherChargeCurrency={isOtherChargeCurrency}
          paymentMethods={paymentMethodPrices}
          priceInChargeCurrency={priceInChargeCurrency}
          selectedPaymentMethod={selectedPaymentMethod}
          shouldAddInstallmentFeeInTotalAmount={shouldAddInstallmentFeeInTotalAmount}
          subTotalPrice={cartPrice}
          totalPrice={cartPrice}
        />
      </Card.Section>
    </Card>
  );
};

Cart.propTypes = {
  cartPrice: PropTypes.number,
  chargeCurrencyPriceFormat: PriceFormat,
  discountAmount: PropTypes.number,
  instalmentFee: PropTypes.number,
  isOtherChargeCurrency: PropTypes.bool,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      label: PropTypes.string,
      price: PropTypes.number,
      serviceFee: PropTypes.number,
    }),
  ),
  priceInChargeCurrency: PropTypes.number,
  products: propTypes.products,
  selectedPaymentMethod: PropTypes.shape({
    label: PropTypes.string,
    price: PropTypes.number,
    serviceFee: PropTypes.number,
    type: PropTypes.string,
  }),
  shouldAddInstallmentFeeInTotalAmount: PropTypes.bool,
  shouldShowAllProducts: PropTypes.bool,
};

const PaymentLinkCart = ({
  cartPrice,
  chargeCurrencyPriceFormat,
  discountAmount,
  headerAndInfoHeight,
  instalmentFee,
  isOtherChargeCurrency,
  paymentMethodPrices,
  priceInChargeCurrency,
  products = [],
  selectedPaymentMethod,
  shouldAddInstallmentFeeInTotalAmount,
  sidebarRef,
}) => {
  const mobileTripDetailsButton = usePortal('mobileTripDetailsButton');

  return (
    <Media query={mediaQueries.large.up}>
      {(matches) =>
        matches ? (
          <StickySidebar containerRef={sidebarRef} topSpacing={headerAndInfoHeight}>
            <Cart
              cartPrice={cartPrice}
              chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
              discountAmount={discountAmount}
              instalmentFee={instalmentFee}
              isOtherChargeCurrency={isOtherChargeCurrency}
              paymentMethodPrices={paymentMethodPrices}
              priceInChargeCurrency={priceInChargeCurrency}
              products={products}
              selectedPaymentMethod={selectedPaymentMethod}
              shouldAddInstallmentFeeInTotalAmount={shouldAddInstallmentFeeInTotalAmount}
              shouldShowAllProducts
            />
          </StickySidebar>
        ) : (
          <>
            {createPortal(
              <Cart
                cartPrice={cartPrice}
                discountAmount={discountAmount}
                instalmentFee={instalmentFee}
                paymentMethodPrices={paymentMethodPrices}
                products={products}
                selectedPaymentMethod={selectedPaymentMethod}
              />,
              mobileTripDetailsButton,
            )}
          </>
        )
      }
    </Media>
  );
};

PaymentLinkCart.propTypes = {
  cartPrice: PropTypes.number,
  chargeCurrencyPriceFormat: PriceFormat,
  discountAmount: PropTypes.number,
  headerAndInfoHeight: PropTypes.number,
  instalmentFee: PropTypes.number,
  isOtherChargeCurrency: PropTypes.bool,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      serviceFee: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  priceInChargeCurrency: PropTypes.number,
  products: propTypes.products,
  selectedPaymentMethod: PropTypes.shape({
    label: PropTypes.string,
    price: PropTypes.number,
    serviceFee: PropTypes.number,
    type: PropTypes.string,
  }),
  shouldAddInstallmentFeeInTotalAmount: PropTypes.bool,
  sidebarRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

const mapStateToProps = (state) => {
  return {
    priceInChargeCurrency: formValueSelector(PAYMENT)(state, PRICE_IN_CHARGE_CURRENCY),
    instalmentFee: formValueSelector(PAYMENT)(state, INSTALMENT_FEE),
    shouldAddInstallmentFeeInTotalAmount: formValueSelector(PAYMENT)(
      state,
      SHOULD_ADD_INSTALMENT_FEE_IN_TOTAL_AMOUNT,
    ),
  };
};

export default connect(mapStateToProps)(PaymentLinkCart);
